import { useEffect, useState } from 'react';
import CurrentLocationIcon from '@/icons/CurrentLocationIcon';
import ErrorIcon from '@/icons/ErrorIcon';
import ButtonCircle from '@/components/ButtonCircle';
import { ThemeContext } from '@/state/Theme';

type LocatorSearchProps = {
  onClickOutside?: () => void;
  isCompact: boolean;
  isGoogleMapsLoaded: boolean;
  googleMaps?: { Geocoder: () => void };
  onSearch: (query: string) => void;
  onUpdateCoords?: ({ latitude, longitude }) => void;
};

const LocatorSearch: React.FC<LocatorSearchProps> = ({
  isCompact,
  isGoogleMapsLoaded,
  googleMaps,
  onSearch,
  onUpdateCoords,
}) => {
  const loadFilterFromQueryString = (query, defaultValue = null) => {
    if ('URLSearchParams' in window) {
      const searchParams = new URLSearchParams(window.location.search);
      let filters = '';

      if (searchParams.has(query)) {
        filters = searchParams.get(query);

        if (typeof defaultValue === 'string') {
          return decodeURI(filters);
        }

        return filters.split('__');
      }
    }

    return defaultValue;
  };
  const [query, setQuery] = useState(loadFilterFromQueryString('q', ''));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const handleSubmit = (event?) => {
    if (event) {
      event.preventDefault();
    }

    // update the URL with the new filters
    if ('URLSearchParams' in window) {
      const searchParams = new URLSearchParams(window.location.search);

      if (query) {
        searchParams.set('q', encodeURI(query));
      } else {
        searchParams.delete('q');
      }

      const newRelativePathQuery = `${
        window.location.pathname
      }?${searchParams.toString()}`;
      window.history.pushState(null, '', newRelativePathQuery);
    }

    onSearch(query);
  };

  const handleUseCurrentLocation = async () => {
    // start loading animation
    setLoading(true);
    setError('');

    const defaultErrorMessage = 'Sorry, there was an error. Please try again.';

    // get the current coords
    window.navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;

        // use google api to determine where we are
        const geocoder = new googleMaps.Geocoder();
        let response;

        try {
          response = await geocoder.geocode({
            location: {
              lat: latitude,
              lng: longitude,
            },
          });
        } catch (e) {
          setError(defaultErrorMessage);
        }

        // stop loader animation
        setLoading(false);

        const { results } = response;

        if (!results || results.length === 0) {
          setError(defaultErrorMessage);
          return;
        }

        // and extract out the region from the result
        const result = results[0];
        const { address_components: addressComponents } = result;

        const region = addressComponents.find((component) =>
          component.types.includes('locality'),
        );

        if (region === undefined || region?.long_name === undefined) {
          setError(defaultErrorMessage);
          return;
        }

        // update the query to our current region
        setQuery(region.long_name);
        onSearch(region.long_name);
        onUpdateCoords({ latitude, longitude });
      },
      (err) => {
        switch (err.code) {
          case 1:
            setError('Please enable location sharing.');
            break;

          default:
            setError(defaultErrorMessage);
            break;
        }

        setLoading(false);
      },
      {
        enableHighAccuracy: true,
      },
    );
  };

  // if we have a default state, we need to trigger a search
  useEffect(() => {
    if (query) {
      handleSubmit();
    }
  }, []);

  return (
    <ThemeContext.Consumer>
      {({ brandStationLabelMobile, brandStationLabelDesktop }) => (
        <div
          className={[
            'locator-search',
            isCompact ? 'locator-search--compact' : null,
          ].join(' ')}
        >
          <div className="locator-search__container">
            <div className="locator-search__interactions">
              <button
                type="button"
                className={[
                  'locator-search__current-location',
                  loading ? 'locator-search__current-location--loading' : null,
                ].join(' ')}
                disabled={!isGoogleMapsLoaded}
                onClick={handleUseCurrentLocation}
              >
                <CurrentLocationIcon size={30} />
                <span className="locator-search__current-location-text">
                  Or use my current location
                </span>
              </button>
              <form className="search-form search-form--stations">
                <input
                  className="search-form__input"
                  name="query"
                  onInput={(e) =>
                    setQuery((e.target as HTMLInputElement).value)
                  }
                  placeholder={
                    window.innerWidth < 768
                      ? brandStationLabelMobile
                      : brandStationLabelDesktop
                  }
                  type="text"
                  value={query}
                />
                <button
                  className="search-form__button button--secondary-alt"
                  onClick={handleSubmit}
                  type="submit"
                  title="Search for location"
                >
                  <ButtonCircle arrowHeight={13} arrowWidth={17} />
                </button>
              </form>
            </div>
            {error && (
              <p className="locator-search__error">
                <ErrorIcon />
                {error}
              </p>
            )}
          </div>
        </div>
      )}
    </ThemeContext.Consumer>
  );
};

export default LocatorSearch;
